/** @module Overlays from the design system: bottom sheet and toast with an undo action. */
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/**
 * A sheet that slides up from the bottom, a centred dialog on wide screens.
 * Built on <dialog>: focus trap and Escape come from the browser.
 */
export function Sheet({
  open,
  onClose,
  label,
  children,
}: {
  open: boolean;
  onClose: () => void;
  label: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement | null>(null);
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  // A tap on the dimmed backdrop closes, like on a phone; the keyboard has Escape.
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    const onClick = (event: MouseEvent) => {
      if (event.target === dialog) onClose();
    };
    dialog.addEventListener("click", onClick);
    return () => {
      dialog.removeEventListener("click", onClick);
    };
  }, [onClose]);
  return (
    <dialog
      ref={ref}
      aria-label={label}
      onClose={onClose}
      className="sheet fixed inset-x-0 top-auto bottom-0 m-0 max-h-[90dvh] w-full max-w-none overflow-y-auto text-(--text) backdrop:bg-(--overlay) md:inset-0 md:m-auto md:h-fit md:max-w-lg md:rounded-[var(--radius-lg)] md:pb-6"
    >
      <div className="sheet-grip md:hidden" />
      {open ? children : null}
    </dialog>
  );
}

interface ToastMessage {
  id: number;
  text: ReactNode;
  icon?: ReactNode;
  action?: { label: string; run: () => void };
  /** Top keeps buttons at the bottom free, e.g. while reviewing one card after another. */
  placement?: "bottom" | "top";
}

interface ToastApi {
  show: (message: Omit<ToastMessage, "id">, durationMs?: number) => void;
}

const ToastContext = createContext<ToastApi>({ show: () => undefined });

/** "Rückgängig" stays this long; the design promises five seconds. */
export const UNDO_MS = 5000;

/** One toast at a time at the bottom of the screen, above the tab bar. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<ToastMessage | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const counter = useRef(0);

  const show = useCallback((message: Omit<ToastMessage, "id">, durationMs = UNDO_MS) => {
    if (timer.current) clearTimeout(timer.current);
    counter.current += 1;
    setCurrent({ ...message, id: counter.current });
    timer.current = setTimeout(() => {
      setCurrent(null);
    }, durationMs);
  }, []);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const api = useMemo(() => ({ show }), [show]);
  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        aria-live="polite"
        className={`pointer-events-none fixed inset-x-0 z-50 flex justify-center px-4 ${
          current?.placement === "top"
            ? "top-[calc(env(safe-area-inset-top)+var(--space-3))]"
            : "bottom-[calc(var(--tabbar-h)+var(--space-4))] md:bottom-6"
        }`}
      >
        {current ? (
          <div key={current.id} className="toast pointer-events-auto w-full max-w-md">
            {current.icon}
            <span className="grow">{current.text}</span>
            {current.action ? (
              <button
                type="button"
                className="font-semibold underline-offset-4 hover:underline"
                onClick={() => {
                  current.action?.run();
                  setCurrent(null);
                }}
              >
                {current.action.label}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  return useContext(ToastContext);
}
